/**
 * RadioSection — composes radio card variants into a panel.
 *
 * Card selection per radio.id:
 *   SINGLE_DUTY_RADIO_IDS → SingleDutyRadioCard (mgmt/engineering, single toggle)
 *   officerVariant=true   → OfficerRadioCard    (all radios in panel, 2-indicator)
 *   officerGroupLabel     → OfficerRadioCard    (only radios in that named group)
 *   default               → RadioCard           (standard 3-row card)
 *
 * Used by SupRadioPanel, OffRadioPanel, OtherRadioPanel.
 */
import { useState, type ReactNode } from 'react'
import { PanelSection, Button } from '../ui'
import { markDirty } from '../../stores/sync'
import type { RadioSlot } from '../../stores/desktop'
import RadioCard from './RadioCard'
import OfficerRadioCard from './OfficerRadioCard'
import SpareRadioCard from './SpareRadioCard'
import SingleDutyRadioCard from './SingleDutyRadioCard'

const SINGLE_DUTY_RADIO_IDS  = ['95', '97', '99', '92', '93', '98', '105', '113', '47', '50', '116', '108', '118']
const AUTO_ONLY_RADIO_IDS    = ['95', '97', '99']
const AUTO_ONLY_ASSIGNMENTS: Record<string, string> = { '95': 'S-01', '97': 'S-03', '99': 'S-02' }

export interface RadioGroup {
  label: string
  ids: string[]
}

interface RadioSectionProps {
  radios: RadioSlot[]
  setRadios: (r: RadioSlot[]) => void
  title: string
  naType: string
  /** When true, ALL non-duty radios in this panel use OfficerRadioCard */
  officerVariant?: boolean
  /** Group label whose radios use OfficerRadioCard */
  officerGroupLabel?: string
  /** Group label whose radios use SpareRadioCard (inline name input on assign) */
  spareGroupLabel?: string
  groups?: RadioGroup[]
}

export default function RadioSection({
  radios, setRadios, title, naType,
  officerVariant, officerGroupLabel, spareGroupLabel, groups,
}: RadioSectionProps) {
  const [showOosMenu, setShowOosMenu] = useState(false)
  const [addInput, setAddInput] = useState('')

  function update(id: string, patch: Partial<RadioSlot>) {
    markDirty()
    setRadios(radios.map(r => (r.id === id ? { ...r, ...patch } : r)))
  }

  function addRadio() {
    const id = addInput.trim()
    if (!id) return
    if (radios.some(r => r.id === id)) { alert('Radio already exists'); return }
    markDirty()
    setRadios([...radios, { id, available: true, assignedTo: '' }])
    setAddInput('')
  }

  function toggleOos(id: string) {
    markDirty()
    setRadios(radios.map(r => (r.id === id ? { ...r, oos: !r.oos } : r)))
  }

  const oosCount   = radios.filter(r => r.oos).length
  const outCount   = radios.filter(r => !r.oos && !r.available && !!r.assignedTo).length
  const availCount = radios.filter(r => !r.oos && r.available).length
  const cols       = radios.length <= 2 ? 2 : 3

  function renderCard(r: RadioSlot, groupLabel?: string) {
    if (SINGLE_DUTY_RADIO_IDS.includes(r.id)) {
      const isAutoOnly = AUTO_ONLY_RADIO_IDS.includes(r.id)
      // Force correct assignedTo for 95/97/99 — never rely on localStorage state
      const fixedRadio = isAutoOnly
        ? { ...r, assignedTo: AUTO_ONLY_ASSIGNMENTS[r.id], available: false }
        : r
      return <SingleDutyRadioCard key={r.id} radio={fixedRadio} readOnly={isAutoOnly} onUpdate={patch => update(r.id, patch)} />
    }
    if (spareGroupLabel && groupLabel === spareGroupLabel) {
      return <SpareRadioCard key={r.id} radio={r} onUpdate={patch => update(r.id, patch)} />
    }
    if (officerVariant || (officerGroupLabel && groupLabel === officerGroupLabel)) {
      return <OfficerRadioCard key={r.id} radio={r} onUpdate={patch => update(r.id, patch)} />
    }
    return <RadioCard key={r.id} radio={r} naType={naType} onUpdate={patch => update(r.id, patch)} />
  }

  const actions = (
    <>
      <input
        type="text"
        value={addInput}
        onChange={e => setAddInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && addRadio()}
        placeholder="Radio ID"
        style={{
          width: 80,
          background: 'var(--bg-deep)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          color: 'var(--text-primary)',
          fontSize: 12,
          padding: '4px 8px',
          outline: 'none',
        }}
      />
      <Button variant="primary" size="sm" onClick={addRadio}>Add</Button>
      <div style={{ position: 'relative' }}>
        <Button variant="ghost" size="sm" onClick={() => setShowOosMenu(m => !m)}>
          Mark OOS
          {oosCount > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}>({oosCount})</span>}
        </Button>
        {showOosMenu && (
          <div style={{
            position: 'absolute', right: 0, top: '110%', width: 220,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
            zIndex: 999, maxHeight: 280, overflowY: 'auto',
          }}>
            <div style={{ padding: '8px 12px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                Select radios to mark OOS
              </div>
              {radios.map(r => (
                <label key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', cursor: 'pointer', borderRadius: 6,
                  fontSize: 13, color: 'var(--text-primary)',
                }}>
                  <input
                    type="checkbox" checked={!!r.oos}
                    onChange={() => toggleOos(r.id)}
                    style={{ width: 15, height: 15, accentColor: '#2563eb' }}
                  />
                  Radio {r.id}
                </label>
              ))}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>
              <Button variant="primary" size="sm" onClick={() => setShowOosMenu(false)}
                style={{ width: '100%', justifyContent: 'center' }}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )

  let body: ReactNode
  if (groups && groups.length > 0) {
    const knownIds = new Set(groups.flatMap(g => g.ids))
    const lastIdx  = groups.length - 1
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {groups.map((group, gi) => {
          const inGroup = radios.filter(r => {
            if (group.ids.includes(r.id)) return true
            if (gi === lastIdx && !knownIds.has(r.id)) return true
            return false
          })
          if (inGroup.length === 0) return null
          return (
            <div key={group.label}>
              <div style={{
                fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: 0.8,
                marginBottom: 8, paddingBottom: 6,
                borderBottom: '1px solid var(--border)',
              }}>
                {group.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
                {inGroup.map(r => renderCard(r, group.label))}
              </div>
            </div>
          )
        })}
      </div>
    )
  } else {
    body = (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
        {radios.map(r => renderCard(r))}
      </div>
    )
  }

  return (
    <PanelSection
      title={title}
      counts={{ available: availCount, out: outCount, oos: oosCount }}
      actions={actions}
    >
      {body}
    </PanelSection>
  )
}
